/**
 * Chat Transports - Custom transport implementations for AI SDK 6
 *
 * These transports transform our backend's raw JSON format to AI SDK's UIMessageChunk format.
 *
 * - LegacyBackendTransport: For standard agent chat (JSON streaming)
 * - MarkdownStreamTransport: For Query Analysis mode (markdown streaming)
 */

import { DefaultChatTransport, type UIMessageChunk } from "ai";
import type { UIMessage } from "@ai-sdk/react";
import type {
  AgentAction,
  Artifact,
  InteractionRequest,
  OpportunityDraftFinding,
  AskUserQuestionPayload,
  TodoItem,
} from "../types";
import type { Citation } from "../../teammate";
import { PromptInputMessage } from "@altimateai/lego";
import { StudioStreamError } from "../utils/studioErrorMessages";
import { workspaceEventSchema, WorkspaceEvent } from "../workspaceEvents";
import { parseAskUserQuestion } from "../utils/askUserQuestion";

// ==========================================================================
// ACCUMULATED STREAM DATA
// During streaming, we extract and store rich metadata that AI SDK doesn't
// natively support. This data is exposed through the hook for V1 compatibility.
// ==========================================================================

/**
 * Read a non-2xx response body and throw a StudioStreamError carrying its
 * `error_code` and HTTP status. Body is discarded on parse failure. Exported
 * so the V1 `/agent/proceed` fallback in `useAgentChat` throws the same shape
 * and gets the HTTP status -> code fallback (e.g. 401 -> session expired).
 */
export const throwHttpError = async (response: Response): Promise<never> => {
  const errorBody = (await response.json().catch(() => null)) as {
    message?: string;
    detail?: string;
    error_code?: string;
  } | null;
  const message = errorBody?.message ?? errorBody?.detail ?? "Failed to send message";
  throw new StudioStreamError(message, {
    code: errorBody?.error_code,
    status: response.status,
  });
};

/**
 * Data accumulated during stream processing.
 * This captures all the rich metadata from agent_outcome and other messages.
 */
export interface AccumulatedStreamData {
  workspaceEvents?: WorkspaceEvent[];
  /** Full text content accumulated from all text deltas */
  content: string;
  /** Actions (info/analysis messages) */
  actions: AgentAction[];
  /** Artifacts from agent_outcome */
  artifacts: Artifact[];
  /** Citations from citations messages */
  citations: Citation[];
  /** Todos from tool_use (TodoWrite) messages */
  todos: TodoItem[];
  /** Interaction prompt from require_user_action */
  interaction: InteractionRequest | undefined;
  /** Heading from agent_outcome */
  heading: string;
  /** Confidence string from agent_outcome */
  confidence_str: string | undefined;
  /** AI-7498: agent-drafted opportunity finding from opportunity_draft */
  opportunityDraft: OpportunityDraftFinding | undefined;
  /** The agent's clarifying question from ask_user_question */
  askUserQuestion: AskUserQuestionPayload | undefined;
}

/**
 * Creates an empty accumulated data object
 */
export const createEmptyAccumulatedData = (): AccumulatedStreamData => ({
  content: "",
  actions: [],
  artifacts: [],
  citations: [],
  todos: [],
  interaction: undefined,
  heading: "",
  confidence_str: undefined,
  opportunityDraft: undefined,
  askUserQuestion: undefined,
});

/**
 * Callback type for receiving accumulated stream data.
 */
export type TransportDataCallback = (_data: Partial<AccumulatedStreamData>) => void;

/**
 * Transport for handling markdown streams (e.g., Query Analysis mode).
 *
 * This class extends DefaultChatTransport to handle markdown streams with
 * data accumulation for V1 compatibility.
 */
export class MarkdownStreamTransport extends DefaultChatTransport<UIMessage> {
  private getBody: () => Record<string, unknown>;
  private apiUrl: string;
  private customHeaders: Record<string, string> | undefined;
  private onData?: TransportDataCallback;
  private currentAbortSignal?: AbortSignal;

  constructor(
    options: ConstructorParameters<typeof DefaultChatTransport>[0] & {
      /**
       * Callback to get fresh body data at request time.
       * This ensures session data (mode, datamates, etc.) is always current.
       */
      getBody?: () => Record<string, unknown>;
      /**
       * Callback to receive accumulated stream data directly.
       */
      onData?: TransportDataCallback;
    }
  ) {
    super(options);
    this.getBody = options?.getBody ?? (() => (options?.body as Record<string, unknown>) || {});
    this.apiUrl = options?.api || "";
    this.customHeaders = options?.headers as Record<string, string> | undefined;
    this.onData = options?.onData;
  }

  /**
   * Emit accumulated data to the registered callback.
   * Checks abort signal to prevent stale callbacks after request cancellation.
   */
  private emitData(data: Partial<AccumulatedStreamData>): void {
    // Don't emit data if request was aborted - prevents stale callbacks
    // from overwriting data after a new request has started
    if (this.currentAbortSignal?.aborted) {
      return;
    }

    if (this.onData) {
      this.onData(data);
    }
  }

  /**
   * Override sendMessages to make the fetch request and process the markdown stream.
   */
  async sendMessages({
    messages,
    abortSignal,
  }: {
    messages: UIMessage[];
    abortSignal?: AbortSignal;
  }): Promise<ReadableStream<UIMessageChunk>> {
    // Store abort signal for checking in emitData
    this.currentAbortSignal = abortSignal;

    const latestUserMessage = [...messages].reverse().find(m => m.role === "user");
    const messageText =
      latestUserMessage?.parts
        ?.filter((p): p is { type: "text"; text: string } => p.type === "text")
        .map(p => p.text)
        .join("\n") || "";

    const freshBody = this.getBody();
    const requestBody = {
      ...freshBody,
      message: messageText,
      mode: "ask",
    };

    const response = await fetch(this.apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...this.customHeaders,
      },
      body: JSON.stringify(requestBody),
      signal: abortSignal,
    });

    if (!response.ok) {
      await throwHttpError(response);
    }

    if (!response.body) {
      throw new Error("No response body for markdown stream");
    }

    return this.processResponseStream(response.body);
  }

  protected processResponseStream(
    stream: ReadableStream<Uint8Array>
  ): ReadableStream<UIMessageChunk> {
    const decoder = new TextDecoder();
    // Capture the abort signal for THIS request at the start of processing.
    // This prevents stale callbacks from emitting data after a new request has started.
    const capturedAbortSignal = this.currentAbortSignal;
    const emitDataForThisRequest = (data: Partial<AccumulatedStreamData>) => {
      // Check the CAPTURED signal, not the current one (which may have been replaced)
      if (capturedAbortSignal?.aborted) {
        return;
      }
      // Call the original emitData (which also has a check, but for belt-and-suspenders)
      this.emitData(data);
    };
    let accumulatedContent = "";
    const messageId = `msg-${Date.now()}`;
    let hasStarted = false;
    let hasEnded = false;

    return new ReadableStream<UIMessageChunk>({
      async start(controller) {
        const reader = stream.getReader();

        try {
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });

            if (!hasStarted && chunk.trim()) {
              controller.enqueue({
                type: "text-start",
                id: messageId,
              });
              hasStarted = true;
            }

            if (chunk) {
              accumulatedContent += chunk;
              controller.enqueue({
                type: "text-delta",
                delta: chunk,
                id: messageId,
              });
              emitDataForThisRequest({ content: accumulatedContent });
            }
          }

          if (hasStarted && !hasEnded) {
            hasEnded = true;

            emitDataForThisRequest({
              content: accumulatedContent,
              actions: [],
              artifacts: [],
              citations: [],
              todos: [],
              interaction: undefined,
              heading: "",
              confidence_str: undefined,
            });

            controller.enqueue({
              type: "text-end",
              id: messageId,
            });
            controller.enqueue({
              type: "finish",
              finishReason: "stop",
            });
          }

          controller.close();
        } catch (error) {
          controller.error(error);
        }
      },
    });
  }
}

/**
 * Custom transport that transforms our backend's raw JSON format
 * to AI SDK's UIMessageChunk format.
 *
 * Our backend sends:
 * - `{"type":"text","content":"..."}` → UIMessageChunk text-delta
 * - `{"type":"tool_use","tool":"..."}` → UIMessageChunk tool-call
 * - `{"type":"agent_outcome","content":"..."}` → UIMessageChunk text-delta + message-end
 * - `{"type":"stream_complete"}` → UIMessageChunk message-end
 *
 * IMPORTANT: Our backend expects legacy format with `message` string field,
 * not AI SDK's `messages` array format.
 */
export class LegacyBackendTransport extends DefaultChatTransport<UIMessage> {
  private getBody: () => Record<string, unknown>;
  private apiUrl: string;
  private customHeaders: Record<string, string> | undefined;
  private onData?: TransportDataCallback;
  private currentAbortSignal?: AbortSignal;

  constructor(
    options: ConstructorParameters<typeof DefaultChatTransport>[0] & {
      /**
       * Callback to get fresh body data at request time.
       * This ensures session data (mode, datamates, etc.) is always current.
       */
      getBody?: () => Record<string, unknown>;
      /**
       * Callback to receive accumulated stream data directly.
       */
      onData?: TransportDataCallback;
    }
  ) {
    super(options);
    this.getBody = options?.getBody ?? (() => (options?.body as Record<string, unknown>) || {});
    this.apiUrl = options?.api || "";
    this.customHeaders = options?.headers as Record<string, string> | undefined;
    this.onData = options?.onData;
  }

  /**
   * Emit accumulated data to the registered callback.
   * Checks abort signal to prevent stale callbacks after request cancellation.
   */
  private emitData(data: Partial<AccumulatedStreamData>): void {
    // Don't emit data if request was aborted - prevents stale callbacks
    // from overwriting data after a new request has started
    if (this.currentAbortSignal?.aborted) {
      return;
    }

    if (this.onData) {
      this.onData(data);
    }
  }

  /**
   * Override sendMessages to use legacy request body format.
   * Our backend expects: { message: string, context: {...}, session_id: string, ... }
   * Not: { messages: [{role, parts}], ... }
   */
  async sendMessages({
    messages,
    abortSignal,
  }: {
    messages: UIMessage[];
    abortSignal?: AbortSignal;
  }): Promise<ReadableStream<UIMessageChunk>> {
    // Store abort signal for checking in emitData
    this.currentAbortSignal = abortSignal;

    const latestUserMessage = [...messages].reverse().find(m => m.role === "user");
    const messageText =
      latestUserMessage?.parts
        ?.filter((p): p is { type: "text"; text: string } => p.type === "text")
        .map(p => p.text)
        .join("\n") || "";
    const files = latestUserMessage?.parts
      ?.filter((p): p is PromptInputMessage["files"][number] => p.type === "file")
      .map((file: PromptInputMessage["files"][number]) => {
        const base64Content = file.url?.split(",")[1] || ""; // Remove data:type;base64, prefix,
        return {
          filename: file.filename || "",
          content: base64Content,
          content_type: file.mediaType || "application/octet-stream",
          size: file.size || base64Content.length,
        };
      });

    const freshBody = this.getBody();

    const legacyRequestBody = {
      ...freshBody,
      context: {
        ...(freshBody.context as Record<string, unknown>),
        uploaded_files: files,
      },
      message: messageText,
      mode: "ask",
    };

    const response = await fetch(this.apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...this.customHeaders,
      },
      body: JSON.stringify(legacyRequestBody),
      signal: abortSignal,
    });

    if (!response.ok) {
      await throwHttpError(response);
    }

    if (!response.body) {
      throw new Error("No response body");
    }

    return this.processResponseStream(response.body);
  }

  protected processResponseStream(
    stream: ReadableStream<Uint8Array>
  ): ReadableStream<UIMessageChunk> {
    const decoder = new TextDecoder();
    // Capture the abort signal for THIS request at the start of processing.
    // This prevents stale callbacks from emitting data after a new request has started.
    const capturedAbortSignal = this.currentAbortSignal;
    const emitDataForThisRequest = (data: Partial<AccumulatedStreamData>) => {
      // Check the CAPTURED signal, not the current one (which may have been replaced)
      if (capturedAbortSignal?.aborted) {
        return;
      }
      // Call the original emitData (which also has a check, but for belt-and-suspenders)
      this.emitData(data);
    };
    let buffer = "";
    const workspaceEvents: WorkspaceEvent[] = [];
    const messageId = `msg-${Date.now()}`;
    let hasStarted = false;
    let hasEnded = false;

    // Accumulated data for V1 compatibility
    let accumulatedContent = "";
    const accumulatedActions: AgentAction[] = [];
    const accumulatedArtifacts: Artifact[] = [];
    const accumulatedCitations: Citation[] = [];
    const accumulatedTodos: TodoItem[] = [];
    let accumulatedInteraction: InteractionRequest | undefined = undefined;
    let accumulatedHeading = "";
    let accumulatedConfidence: string | undefined = undefined;
    let accumulatedOpportunityDraft: OpportunityDraftFinding | undefined = undefined;
    let accumulatedAskUserQuestion: AskUserQuestionPayload | undefined = undefined;

    return new ReadableStream<UIMessageChunk>({
      async start(controller) {
        const reader = stream.getReader();

        try {
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;

            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            for (const line of lines) {
              const trimmed = line.trim();
              if (!trimmed) continue;

              try {
                const msg = JSON.parse(trimmed);

                if (!hasStarted) {
                  controller.enqueue({
                    type: "text-start",
                    id: messageId,
                  });
                  hasStarted = true;
                }

                switch (msg.type) {
                  case "workspace_context":
                  case "workspace_status": {
                    const event = workspaceEventSchema.safeParse(msg);
                    if (event.success) {
                      workspaceEvents.push(event.data);
                      emitDataForThisRequest({ workspaceEvents: [...workspaceEvents] });
                    }
                    break;
                  }
                  case "text":
                    if (msg.content) {
                      const textAction: AgentAction = {
                        id: msg.id || `action-${Date.now()}-${accumulatedActions.length}`,
                        type: "info",
                        content: msg.content,
                        timestamp: Date.now(),
                      };
                      accumulatedActions.push(textAction);
                      emitDataForThisRequest({
                        actions: [...accumulatedActions],
                      });
                    }
                    break;

                  case "info":
                  case "analysis":
                    if (msg.content) {
                      const action: AgentAction = {
                        id: msg.id || `action-${Date.now()}`,
                        type: msg.type as "info" | "analysis",
                        content: msg.content,
                        timestamp: Date.now(),
                        artifacts: msg.artifacts,
                      };
                      accumulatedActions.push(action);
                      emitDataForThisRequest({
                        actions: [...accumulatedActions],
                      });
                    }
                    break;

                  case "agent_outcome":
                  case "final_response":
                    if (msg.content) {
                      accumulatedContent = msg.content;
                      controller.enqueue({
                        type: "text-delta",
                        delta: msg.content,
                        id: messageId,
                      });
                    }

                    if (msg.artifacts && Array.isArray(msg.artifacts)) {
                      accumulatedArtifacts.push(...msg.artifacts);
                    }

                    if (msg.heading) {
                      accumulatedHeading = msg.heading;
                    }

                    if (msg.confidence_str) {
                      accumulatedConfidence = msg.confidence_str;
                    }

                    emitDataForThisRequest({
                      content: accumulatedContent,
                      artifacts: [...accumulatedArtifacts],
                      heading: accumulatedHeading,
                      confidence_str: accumulatedConfidence,
                    });
                    break;

                  case "tool_use": {
                    const toolName = msg.tool || "unknown";
                    const toolCallId = `tool-${messageId}-${Date.now()}`;
                    const toolInput = msg.todos || msg.input || {};

                    if (toolName === "TodoWrite" && msg.todos && Array.isArray(msg.todos)) {
                      accumulatedTodos.length = 0;
                      accumulatedTodos.push(...msg.todos);
                      emitDataForThisRequest({ todos: [...accumulatedTodos] });
                    }

                    controller.enqueue({
                      type: "tool-input-start",
                      toolCallId,
                      toolName,
                    });

                    controller.enqueue({
                      type: "tool-input-delta",
                      toolCallId,
                      inputTextDelta: JSON.stringify(toolInput),
                    });

                    controller.enqueue({
                      type: "tool-input-available",
                      toolCallId,
                      toolName,
                      input: toolInput,
                    });
                    break;
                  }

                  case "citations":
                    if (msg.citations && Array.isArray(msg.citations)) {
                      accumulatedCitations.push(...msg.citations);
                      emitDataForThisRequest({
                        citations: [...accumulatedCitations],
                      });
                    }
                    break;

                  case "require_user_action":
                    if (msg.interaction) {
                      accumulatedInteraction = msg.interaction as InteractionRequest;
                      emitDataForThisRequest({
                        interaction: accumulatedInteraction,
                      });
                      controller.enqueue({
                        type: "text-delta",
                        delta: `\n[Interaction Required: ${msg.interaction.type}]\n${msg.interaction.prompt}\n`,
                        id: messageId,
                      });
                    }
                    break;

                  case "ask_user_question": {
                    // The agent's clarifying question; the turn
                    // renders it as a question card.
                    const question = parseAskUserQuestion(msg.content);
                    if (question) {
                      accumulatedAskUserQuestion = question;
                      emitDataForThisRequest({ askUserQuestion: question });
                    }
                    break;
                  }

                  case "opportunity_draft":
                    // AI-7498: the producer streams the drafted finding; the
                    // turn renders it as an editable form.
                    if (msg.finding) {
                      accumulatedOpportunityDraft = msg.finding as OpportunityDraftFinding;
                      emitDataForThisRequest({
                        opportunityDraft: accumulatedOpportunityDraft,
                        askUserQuestion: accumulatedAskUserQuestion,
                      });
                    }
                    break;

                  case "error":
                    // Error the stream with a structured carrier so the code
                    // and retryable flag reach useAgentChat.onError (AI-7467).
                    // The AI SDK error chunk only carries a string, so we can't
                    // ride the fields on the chunk. This is terminal; the
                    // trailing stream_complete line is intentionally skipped.
                    controller.error(
                      new StudioStreamError(msg.content || msg.error || "Unknown error", {
                        code: msg.error_code,
                        retryable: msg.retryable,
                      })
                    );
                    return;

                  case "complete":
                  case "stream_complete":
                    if (!hasEnded) {
                      hasEnded = true;

                      emitDataForThisRequest({
                        content: accumulatedContent,
                        actions: [...accumulatedActions],
                        artifacts: [...accumulatedArtifacts],
                        citations: [...accumulatedCitations],
                        todos: [...accumulatedTodos],
                        interaction: accumulatedInteraction,
                        heading: accumulatedHeading,
                        confidence_str: accumulatedConfidence,
                        opportunityDraft: accumulatedOpportunityDraft,
                        askUserQuestion: accumulatedAskUserQuestion,
                      });

                      controller.enqueue({
                        type: "text-end",
                        id: messageId,
                      });
                      controller.enqueue({
                        type: "finish",
                        finishReason: "stop",
                      });
                    }
                    break;

                  default:
                    // Unknown message type - ignore
                    break;
                }
              } catch {
                // Not JSON - might be markdown stream, pass as text
                if (trimmed && !trimmed.startsWith("{")) {
                  if (!hasStarted) {
                    controller.enqueue({
                      type: "text-start",
                      id: messageId,
                    });
                    hasStarted = true;
                  }
                  accumulatedContent += trimmed + "\n";
                  controller.enqueue({
                    type: "text-delta",
                    delta: trimmed + "\n",
                    id: messageId,
                  });
                  emitDataForThisRequest({ content: accumulatedContent });
                }
              }
            }
          }

          // End message if we started but didn't get explicit end
          if (hasStarted && !hasEnded) {
            hasEnded = true;

            emitDataForThisRequest({
              content: accumulatedContent,
              actions: [...accumulatedActions],
              artifacts: [...accumulatedArtifacts],
              citations: [...accumulatedCitations],
              todos: [...accumulatedTodos],
              interaction: accumulatedInteraction,
              heading: accumulatedHeading,
              confidence_str: accumulatedConfidence,
              opportunityDraft: accumulatedOpportunityDraft,
              askUserQuestion: accumulatedAskUserQuestion,
            });

            controller.enqueue({
              type: "text-end",
              id: messageId,
            });
            controller.enqueue({
              type: "finish",
              finishReason: "stop",
            });
          }

          controller.close();
        } catch (error) {
          controller.error(error);
        }
      },
    });
  }
}
